/**
 * @desc Funnelback Dynamic Facet Switcher & Renderer
 * Handles client-side facet toggling, dynamic auto-vivification,
 * and renders facets with live document counts from Funnelback API responses.
 */

// Helper: Format raw category values for clean UI display
export const formatFacetLabel = (rawVal) => {
    if (!rawVal) return "";
    const val = rawVal.trim().toLowerCase();

    const specialCases = {
        "north america": "North America",
        "middle east": "Middle East",
        "south america": "South America",
        "australia and pacific": "Australia & Pacific",
        "downstream and refining": "Downstream & refining",
        "hydrogen and ammonia": "Hydrogen & ammonia",
        "digital transformation and ai": "Digital transformation & AI",
        "epc and epcm": "EPC & EPCM",
        "sme": "SME",
        "smes": "SMEs",
        "oil _ampersand_ gas - midstream": "Oil & Gas - Midstream",
        "distribution _ampersand_ storage": "Distribution & Storage",
        "mining _ampersand_ minerals": "Mining & Minerals"
    };

    if (specialCases[val]) {
        return specialCases[val];
    }

    // Replace _ampersand_ and _comma_ if present
    let formatted = rawVal
        .replace(/_ampersand_/gi, "&")
        .replace(/_comma_/gi, ",");

    // Capitalize first letter
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

// Energy sectors for two-column Sector dropdown rendering
const ENERGY_SECTORS = [
    "battery energy storage",
    "biofuels",
    "carbon capture",
    "downstream and refining",
    "hydrogen and ammonia",
    "midstream",
    "nuclear",
    "power",
    "solar",
    "upstream",
    "wind"
];

/**
 * Dynamically render facet options with live counts into container placeholders
 * @param {Array} facets - List of facet objects from Funnelback response
 * @param {Object} searchInstance - Active Funnelback search instance (e.g. window.searchCS)
 */
// Cache baseline counts from initial search to prevent (null) display when Funnelback
// returns count: null for selected multiselect OR facets
const facetCountsCache = {};

const getFacetCountBadge = (facetName, item, isSelected) => {
    const key = `${(facetName || '').toLowerCase()}__${(item.data || '').toLowerCase()}`;
    if (item.count !== null && item.count !== undefined) {
        facetCountsCache[key] = item.count;
    }
    const countVal = (item.count !== null && item.count !== undefined)
        ? item.count
        : facetCountsCache[key];

    const isDisabled = countVal === 0 && !isSelected;
    const badgeHtml = (countVal !== null && countVal !== undefined)
        ? `<span class="facet-count">(${countVal})</span>`
        : '';

    return { countVal, isDisabled, badgeHtml };
};

// Sort facet items alphabetically by their formatted display label (A-Z)
const sortItemsByLabel = (items) => {
    return items.slice().sort((a, b) => {
        const labelA = formatFacetLabel(a.label || a.data || '');
        const labelB = formatFacetLabel(b.label || b.data || '');
        return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
    });
};

export const renderDynamicFacets = (facets, searchInstance) => {
    if (!facets || !facets.length) return;

    // Find all dynamic facet container placeholders in the DOM
    const containers = document.querySelectorAll("[data-facet-container]");
    if (!containers.length) return;

    const searchVarName = (searchInstance && searchInstance.config && searchInstance.config.varName) ||
        Object.keys(window).find(k => window[k] === searchInstance) ||
        "searchCS";

    containers.forEach((container) => {
        const targetFacetName = container.getAttribute("data-facet-container");
        if (!targetFacetName) return;

        // Locate matching facet from Funnelback JSON
        const facetData = facets.find(
            (f) => f.name && f.name.toLowerCase() === targetFacetName.toLowerCase()
        );
        if (!facetData || !facetData.allValues || !facetData.allValues.length) return;

        const paramName =
            (facetData.categories && facetData.categories[0] && facetData.categories[0].queryStringParamName) ||
            `f.${facetData.name}|categorisation${facetData.name}`;

        const activeBucket = searchInstance?.search?.activeFacets?.[paramName]?.value || [];
        const activeVals = Array.isArray(activeBucket)
            ? activeBucket.map((v) => String(v).toLowerCase())
            : [String(activeBucket).toLowerCase()];

        // Filter out "All" meta-category if returned by Funnelback
        const validValues = facetData.allValues.filter(
            (v) => v.data && v.data.toLowerCase() !== "all" && v.label && v.label.toLowerCase() !== "all"
        );

        const facetKey = targetFacetName.toLowerCase();

        // Check if this is the Sectors dropdown needing two columns (Energy vs Materials)
        if (facetKey === "sectors") {
            const energyItems = [];
            const materialsItems = [];

            validValues.forEach((item) => {
                const itemVal = item.data.toLowerCase();
                if (ENERGY_SECTORS.includes(itemVal)) {
                    energyItems.push(item);
                } else {
                    materialsItems.push(item);
                }
            });

            const sortedEnergy = sortItemsByLabel(energyItems);
            const sortedMaterials = sortItemsByLabel(materialsItems);

            const renderList = (items, colPrefix) => {
                let html = '<ul>';
                items.forEach((item, idx) => {
                    const inputId = `${searchVarName}_sec_${colPrefix}_${idx}`;
                    const isSelected = item.selected || activeVals.includes(item.data.toLowerCase());
                    const { isDisabled, badgeHtml } = getFacetCountBadge(targetFacetName, item, isSelected);
                    const displayLabel = formatFacetLabel(item.label || item.data);

                    html += `
                        <li class="${isSelected ? 'dropdownActive' : ''} ${isDisabled ? 'is-disabled' : ''}">
                            <input type="checkbox"
                                   name="${inputId}"
                                   id="${inputId}"
                                   value="${idx}"
                                   class="sq-form-field fbjs-facet-switcher"
                                   data-facet-query-string-param-name="${paramName}"
                                   data-facet-selected-category-values="${item.data}"
                                   data-fbjs-variable-name="${searchVarName}"
                                   ${isSelected ? 'checked' : ''}
                                   ${isDisabled ? 'disabled' : ''}>
                            <label for="${inputId}">
                                <span class="facet-label">${displayLabel}</span>
                                ${badgeHtml}
                            </label>
                        </li>`;
                });
                html += '</ul>';
                return html;
            };

            container.innerHTML = `
                <div class="dropdown-list__two-columns">
                    <div class="dropdown-list__column">
                        <div class="dropdown-list__column-title">Energy</div>
                        ${renderList(sortedEnergy, 'eng')}
                    </div>
                    <div class="dropdown-list__column">
                        <div class="dropdown-list__column-title">Materials</div>
                        ${renderList(sortedMaterials, 'mat')}
                    </div>
                </div>`;
        } else {
            // Single-column dropdown (Geography, Solutions, etc.)
            const sortedValues = sortItemsByLabel(validValues);
            let listHtml = '<ul>';
            sortedValues.forEach((item, idx) => {
                const prefix = targetFacetName.toLowerCase().slice(0, 3);
                const inputId = `${searchVarName}_${prefix}_${idx}`;
                const isSelected = item.selected || activeVals.includes(item.data.toLowerCase());
                const { isDisabled, badgeHtml } = getFacetCountBadge(targetFacetName, item, isSelected);
                const displayLabel = formatFacetLabel(item.label || item.data);

                listHtml += `
                    <li class="${isSelected ? 'dropdownActive' : ''} ${isDisabled ? 'is-disabled' : ''}">
                        <input type="checkbox"
                               name="${inputId}"
                               id="${inputId}"
                               value="${idx}"
                               class="sq-form-field fbjs-facet-switcher"
                               data-facet-query-string-param-name="${paramName}"
                               data-facet-selected-category-values="${item.data}"
                               data-fbjs-variable-name="${searchVarName}"
                               ${isSelected ? 'checked' : ''}
                               ${isDisabled ? 'disabled' : ''}>
                        <label for="${inputId}">
                            <span class="facet-label">${displayLabel}</span>
                            ${badgeHtml}
                        </label>
                    </li>`;
            });
            listHtml += '</ul>';
            container.innerHTML = listHtml;
        }

        // Notify parent dropdown container to update selection header badge
        container.dispatchEvent(new CustomEvent("facetsRendered", { bubbles: true }));
    });
};

/**
 * Handle checkbox facet click with dynamic auto-initialization and defensive typing
 */
export const handleFacetToggle = (target) => {
    const fbjsVariableName = target.getAttribute("data-fbjs-variable-name");
    const searchFacetName = target.getAttribute("data-facet-query-string-param-name");
    const rawFacetVals = target.getAttribute("data-facet-selected-category-values");

    if (!fbjsVariableName || !searchFacetName || !rawFacetVals) {
        return;
    }

    const searchInstance = window[fbjsVariableName];
    if (!searchInstance || !searchInstance.search) {
        console.warn(`[FBJS Facet] Search instance window['${fbjsVariableName}'] not found.`);
        return;
    }

    // 1. DYNAMIC AUTO-INITIALIZATION:
    // Ensure activeFacets and the specific parameter bucket exist
    if (!searchInstance.search.activeFacets) {
        searchInstance.search.activeFacets = {};
    }
    if (!searchInstance.search.activeFacets[searchFacetName]) {
        searchInstance.search.activeFacets[searchFacetName] = {
            param: searchFacetName,
            value: []
        };
    }

    // 2. DEFENSIVE TYPE NORMALIZATION:
    // Ensure .value is always a mutable Array (preventing TypeError on direct URL landings)
    const bucket = searchInstance.search.activeFacets[searchFacetName];
    if (!Array.isArray(bucket.value)) {
        bucket.value = bucket.value ? [bucket.value] : [];
    }

    const searchFacetVals = rawFacetVals
        .replace(/&/gi, "&amp;")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

    // 3. CASE-INSENSITIVE TOGGLE LOGIC:
    const isAlreadySelected = searchFacetVals.some((targetVal) =>
        bucket.value.some((v) => v.toLowerCase() === targetVal.toLowerCase())
    );

    if (isAlreadySelected) {
        // Deselect: remove matched values
        searchFacetVals.forEach((targetVal) => {
            const idx = bucket.value.findIndex((v) => v.toLowerCase() === targetVal.toLowerCase());
            if (idx > -1) {
                bucket.value.splice(idx, 1);
            }
        });
        target.checked = false;
        target.closest("li")?.classList.remove("dropdownActive");
    } else {
        // Select: push values
        searchFacetVals.forEach((targetVal) => {
            if (!bucket.value.some((v) => v.toLowerCase() === targetVal.toLowerCase())) {
                bucket.value.push(targetVal);
            }
        });
        target.checked = true;
        target.closest("li")?.classList.add("dropdownActive");
    }

    // Reset pagination to page 1 on filter change
    searchInstance.search.startRank = 1;

    // Trigger Funnelback search & URL update
    if (typeof searchInstance.doSearch === "function") {
        searchInstance.doSearch(true, true);
    }
};

/**
 * Initialize event delegation for facet switchers
 */
export const fbjsFacetSwitcher = async () => {
    // Event delegation on document: handles both static and dynamically rendered checkboxes
    if (!window._fbjsFacetSwitcherInitialized) {
        document.addEventListener("click", (e) => {
            // Case 1: Direct click on checkbox input
            if (e.target.matches("input[type=checkbox].fbjs-facet-switcher")) {
                handleFacetToggle(e.target);
                return;
            }

            // Case 2: Click on label, span, count badge, or li row containing a facet switcher checkbox
            const li = e.target.closest("li");
            const checkbox = li?.querySelector("input[type=checkbox].fbjs-facet-switcher");
            if (checkbox && !checkbox.disabled) {
                e.preventDefault();
                handleFacetToggle(checkbox);
                checkbox.dispatchEvent(new Event("change", { bubbles: true }));
            }
        });
        window._fbjsFacetSwitcherInitialized = true;
    }

    hookSearchPrototype();
};

export const hookSearchPrototype = () => {
    // Hook Funnelback Search prototype to auto-render dynamic facets on all pages
    if (typeof window !== "undefined" && typeof window.Search !== "undefined" && window.Search.default && window.Search.default.prototype) {
        const proto = window.Search.default.prototype;
        if (!proto._dynamicFacetsHooked) {
            const origPrintFacets = proto.printFacets;
            proto.printFacets = function (...args) {
                const res = origPrintFacets ? origPrintFacets.apply(this, args) : undefined;
                const facets = this.facetsRestricted || this.facets || this.data?.response?.facets;
                if (facets && facets.length) {
                    renderDynamicFacets(facets, this);
                }
                return res;
            };
            proto._dynamicFacetsHooked = true;
        }
    }
};

if (typeof window !== "undefined") {
    window._hookSearchPrototype = hookSearchPrototype;
}

/**
 * Handle unticking active facet chip spans
 */
export const fbjsFacetSwitcherUnticker = () => {
    const activeFacets = document.querySelector(".c-filters .active-facets");
    if (activeFacets && !activeFacets._untickerBound) {
        activeFacets.addEventListener("click", (e) => {
            const span = e.target.closest("span[param][name]");
            if (span) {
                const param = span.getAttribute("param");
                const name = (span.getAttribute("name") || "").toLowerCase();

                if (param === "date") {
                    span.remove();
                    document.querySelectorAll(".dropdown-list.date-horizon, [data-date-container]").forEach((c) => {
                        const allLi = c.querySelector('li[data-date-range="all"]');
                        if (allLi) allLi.click();
                    });
                    return;
                }

                document.querySelectorAll(".dropdown-list input[type=checkbox]").forEach((input) => {
                    const inputParam = input.getAttribute("data-facet-query-string-param-name");
                    const inputVals = (input.getAttribute("data-facet-selected-category-values") || "")
                        .toLowerCase()
                        .split(",")
                        .map((v) => v.trim());

                    if (inputParam === param && inputVals.includes(name)) {
                        input.checked = false;
                        input.closest("li")?.classList.remove("dropdownActive");
                    }
                });
            }
        });
        activeFacets._untickerBound = true;
    }
};
